import BigNumber from 'bignumber.js'
import { format, formatDuration, intervalToDuration } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
import { HiOutlineChevronDown } from 'react-icons/hi'

import { Button } from '@/components/atoms/Button'
import { Card } from '@/components/atoms/Card'
import {
  ExpandedCopyWrapper,
  LargeExpandedCopyWrapper,
} from '@/components/atoms/CopyWrapper'
import { FadeInWhenVisible } from '@/components/atoms/FadeInWhenVisible'
import { Image } from '@/components/atoms/Image'
import { Link } from '@/components/atoms/Link'
import { Spacing } from '@/components/atoms/Spacing'
import { NavAndFooterLayout } from '@/components/layouts/NavAndFooterLayout'
import { CopyText, CopyTitle, Span, Text } from '@/components/typography'
import { formatToUSD } from '@/helpers/currency'
import { useSunsetPreferencesQuery } from '@/queries/legacy/sunsetPreferences.legacy.graphql'

const getFAQs = (
  date: string,
  networkFeeWaivedThreshold:
    | string
    | null
    | undefined = '{Loading fee threshold...}'
): [React.ReactNode, React.ReactNode][] => [
  [
    'What will happen to my Dharma app? Will I still be able to buy / sell / swap?',
    <>
      <ul>
        <li>
          Between now and {date}, you will only be able to transfer your tokens
          out of your Dharma wallet, or sell your tokens and receive dollars in
          your bank account. Only actions that transfer tokens out of Dharma
          will be available.
        </li>
        <li>
          You will <u>not</u> be able to buy tokens, perform token-to-token
          swaps, or invest in DeFi protocols. After {date}, the Dharma app will
          no longer exist, and you will no longer be able to access the Dharma
          App. You need to remove your tokens from the account before {date}
        </li>
      </ul>
    </>,
  ],
  [
    'What will happen to my assets?',
    'If you want to keep your tokens, you can transfer them to a different, non-Dharma, crypto wallet. If you don’t want to keep your tokens and would rather receive USD, you can sell your tokens and receive USD via ACH to your bank account.',
  ],
  [
    'What do I do now to transfer all my assets off of Dharma?',
    <ul key="1">
      <li>Here are the steps to transfer or sell your assets:</li>
      <ul>
        <li>
          Open your Dharma app and go to the home screen where your assets are
          listed.
        </li>
        <li>
          Click into the specific asset you are looking to sell / transfer.
        </li>
        <li>
          Once you click into the asset, you’ll see two options at the bottom,
          ‘Sell to Bank’ or ‘Transfer’. Click your preferred solution there.
        </li>
        <ul>
          <li>
            <i>NOTE</i>: If transferring to another wallet, PLEASE DOUBLE CHECK
            that you are transferring to a wallet on the same network. Only
            tokens from Ethereum Mainnet will arrive to other Ethereum supported
            wallets, and only tokens from Polygon will arrive to other Polygon
            supported wallets. You will lose your funds if you send tokens to an
            address on the wrong network!
          </li>
        </ul>
        <li>
          Follow the prompts to complete the process. Please note that you will
          only be able to transfer or sell the maximum amount of tokens you have
          in your wallet. You cannot transfer some and sell some, you must
          perform a single action for 100% of your holdings of each token.
        </li>
        <li>
          Once complete for that asset, go into the next asset and replicate the
          same process.{' '}
          <b>
            You will need to do this for every asset on both the Ethereum
            Mainnet and Polygon networks.
          </b>
        </li>
      </ul>
    </ul>,
  ],
  [
    'Am I going to have to pay gas fees for all these transfers?!?!?!',
    `So glad you asked– Dharma will be covering gas fees for all transfers and sales over $${networkFeeWaivedThreshold}. If your balance of a token is less than $${networkFeeWaivedThreshold}, you can still transfer the tokens out or sell them, but you will need to pay the network fees yourself.`,
  ],
  [
    'Does Dharma have a suggested wallet we should transfer over to?',
    <>
      Our team plans to transfer our tokens to{' '}
      <a href="https://metamask.io/">Metamask</a> or{' '}
      <a href="https://www.coinbase.com/wallet">Coinbase Wallet</a>. If you are
      comfortable using a hardware wallet,{' '}
      <a href="https://www.ledger.com/">Ledger</a> and{' '}
      <a href="https://trezor.io/">Trezor</a> are the two most popular options,
      though there are many alternatives. Please do your own research on which
      wallets work best for you. We have confirmed that all of the options we
      have suggested are able to receive transfers from smart contract wallets
      like Dharma, but if you choose a different wallet, please confirm that the
      wallet can receive funds from a smart contract wallet.
    </>,
  ],
  [
    <>
      Are there any wallets I can <i>not</i> send my assets to?
    </>,
    <>
      We are not able to confirm for all wallets on all chains.{' '}
      <b>Please do your own research!</b> With that said, most Ethereum wallets
      should be able to support receiving assets from your Dharma wallets. Here
      are some exceptions to be careful with:
      <ul>
        <li>
          Centralized Exchanges (e.g. Coinbase, Binance, KuCoin) - Centralized
          exchanges typically do not support receiving assets on Polygon
          network. Similarly many centralized exchanges do not support detecting
          ETH or ERC-20 tokens that are deposited in from a smart contract
          wallet. Please be careful when making transfers to Centralized
          Exchanges.
        </li>
        <li>
          Smart Contract Wallets (e.g. Argent) - if a smart contract wallet is
          not deployed on the same network you are trying to send assets on, it
          will not be able to receive the funds you’ve sent it. For instance,
          Argent (an excellent Ethereum smart contract wallet) is not deployed
          on Polygon — meaning you will lose your tokens if you try to send them
          to your Argent address on Polygon.
        </li>
      </ul>
      <b>
        The safest thing you can do is transfer your assets to a non-custodial
        wallet like MetaMask or to a hardware wallet.
      </b>
    </>,
  ],
  [
    'How long will the process take until my assets are sent to my new wallet?',
    <>
      <ul>
        <li>
          If you are transferring your assets from Dharma to another crypto
          wallet, the transaction will confirm within 1-2 minutes (as fast as
          the network processes transactions).
        </li>
        <li>
          If you are selling your assets to your bank, it will take 3-5 days for
          the funds to hit your account.
        </li>
      </ul>
    </>,
  ],
  [
    'Am I able to continue purchasing on Dharma in the interim?',
    'No, you will not be able to make new purchases in the Dharma app. The only features available will be transferring out and selling assets.',
  ],
  [
    'Can I send different tokens to different wallets? Or does everything need to be sent to one wallet?',
    'Because we are paying for your network fees, we require that you transfer the full balance of a given token. Thus, each asset can only be sent to a single wallet, but you can choose to transfer different assets to different wallets. Please ensure that you are sending each asset to a wallet that supports that asset and the network that the asset is on.',
  ],
  [
    'Once my tokens arrive in my new wallet, is there anything I need to do?',
    'No, you’ll be all set! Just ensure that all of your assets from both networks in your Dharma wallet have been transferred out :)',
  ],
  [
    'What if I just want to sell my Dharma tokens to my bank account and not transfer them, can I do that?',
    `Yes you can, we’ll also cover the fees for that if the balance of the asset is over $${networkFeeWaivedThreshold}!`,
  ],
  [
    'What’s the deadline to get this all completed by?',
    `The deadline to complete all sales and transfers is ${date}`,
  ],
  [
    'Will I be able to use the Dharma app until the deadline?',
    'You will be able to remove your funds from the app via ACH withdrawal or by transfer, but you will not be able to purchase more tokens or swap them.',
  ],
  [
    'What is going to happen with the Dharma app, and Dharma the company?',
    `The Dharma app is being sunset and will no longer be available beyond ${date}. The Dharma company and our team will integrate with OpenSea, and become part of the OpenSea team!`,
  ],
  [
    'Will I still be able to access my wallet after February 18th?',
    <>
      <b>TL;DR</b>: Technically, yes, but it&apos;ll be hard and you won&apos;t
      be able to use the Dharma app —{' '}
      <b>
        we highly, highly recommend withdrawing or selling your assets before
        then at no cost
      </b>
      .
      <ul>
        <li>
          At the conclusion of the 30 day sunset period, the Dharma team will
          renounce any remaining control of the Dharma Smart Wallet.
        </li>
        <li>
          Users who have not completed off-boarding will still be able to access
          funds in their wallet through an open-source DApp that will be
          permanently hosted through IPFS in a wholly non-custodial experience.
        </li>
        <li>
          However, accessing funds through the DApp will be a relatively
          technical experience in which users will have to pay their own gas
          fees, will not have access to customer support assistance if needed,
          and will have slightly degraded security assurances on their funds.
        </li>
        <li>
          <b>
            This DApp is intended to be a &quot;fallback&quot; mechanism in case
            users don&apos;t get their assets out in time — we highly discourage
            you from intentionally leaving your assets in the wallet!
          </b>
        </li>
      </ul>
    </>,
  ],
  [
    'Why is Dharma being acquired by OpenSea?',
    'Since day 1, Dharma’s mission has always been to make crypto accessible to the masses. 2021 was the year where millions of new crypto users joined the web3 ecosystem, many by way of NFTs through OpenSea. We believe our mission of bringing crypto to the masses will have a significantly larger reach with OpenSea.',
  ],
  [
    'What if I want to sell to a bank account but I haven’t linked my bank account yet? Can I link a new bank account?',
    'Yes you will be able to link a new bank account to withdraw your funds, however you won’t be able to make any new purchases.',
  ],
  [
    'Why do I need to go through Account Recovery?',
    <>
      <ul>
        <li>
          You will need to go through Account Recovery if the Dharma app is not
          connected to your wallet on your current device. This can happen for
          various reasons (including you getting a new phone), and is simple to
          fix.
        </li>
        <li>To go through the Account Recovery process:</li>
        <ul>
          <li>Open up the Dharma app</li>
          <li>Click the ‘Settings’ button on the top right</li>
          <li>
            Tap “wallet disconnected” to begin the Account Recovery process.
          </li>
        </ul>
      </ul>
    </>,
  ],
  [
    'What should  I do with NFTs that are stuck in my wallet?',
    <>
      You can still use WalletConnect functionality in the Dharma app during the
      sunset period. Take the following steps to transfer your NFTs to a
      different wallet:
      <ol>
        <li>Connect your wallet to opensea.io through WalletConnect</li>
        <li>Go to your Profile</li>
        <li>Click the NFT you want to transfer</li>
        <li>
          Use the OpenSea transfer flow to initiate a transfer to a different
          wallet
        </li>
        <li>Approve the transaction on your Dharma wallet</li>
      </ol>
    </>,
  ],
  [
    'I still have questions. How do I get them answered?',
    <>
      Don’t worry– Support is here for you. For any questions or issues, please
      email <a href="mailto:support@dharma.io">support@dharma.io</a> with detail
      and we will get back to you within 48 hours. Alternatively, you can go
      into <a href="https://discord.com/invite/uCYfbUytqY">our Discord</a> and
      write into the #Support channel.
    </>,
  ],
]

interface FAQDropdownProps {
  question: React.ReactNode
  text: React.ReactNode
}

const FAQDropdown: React.FC<FAQDropdownProps> = ({ question, text }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Button buttonType="unstyled" block>
      <Spacing $borderedBottom>
        <Spacing
          $verticalSize="xxlarge"
          $flexDirection="row"
          $justifyContent="space-between"
          $alignItems="center"
          onClick={() => setIsOpen(!isOpen)}
          css={`
            @media (max-width: 539px) {
              padding-top: 20px;
              padding-bottom: 20px;
            }
          `}
        >
          <CopyTitle level={4} textAlign="left" css="padding-right: 10px">
            {question}
          </CopyTitle>
          <Span accented>
            <HiOutlineChevronDown
              size={36}
              style={isOpen ? { transform: 'rotate(180deg)' } : {}}
            />
          </Span>
        </Spacing>
        {isOpen && (
          <Spacing $bottomSize="xxlarge">
            <CopyText textAlign="left">{text}</CopyText>
          </Spacing>
        )}
      </Spacing>
    </Button>
  )
}

interface EmojiWithTextEntryProps {
  emoji: string
  text: React.ReactNode
  bold?: boolean
}

const EmojiWithTextEntry: React.FC<EmojiWithTextEntryProps> = ({
  bold,
  emoji,
  text,
}) => (
  <Spacing $flexDirection="row" $alignItems="flex-start">
    <Text size="smedium" css="padding-top: 4px; width: 35px; flex-shrink: 0;">
      {emoji}
    </Text>
    <Text size="smedium" bold={bold}>
      {text}
    </Text>
  </Spacing>
)

export const Home = (): JSX.Element => {
  const [sunsetEndsIn, setSunsetEndsIn] = useState('...')
  const [sunsetEndsInDays, setSunsetEndsInDays] = useState('...')
  const { data } = useSunsetPreferencesQuery()
  const sunsetEndDateRef = useRef<string | null | undefined>()
  const sEndDate = data?.getSPreferences?.sEndDate
  sunsetEndDateRef.current = sEndDate
  const networkFeeWaivedThreshold =
    data?.getSPreferences?.networkFeeWaivedThreshold
  const endDate = sEndDate
    ? format(new Date(sEndDate), "MMMM do 'at' h:mm b '('O')'")
    : '{Loading end date...}'

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      const sunsetEndDuration = intervalToDuration({
        start: now,
        end: sunsetEndDateRef.current
          ? new Date(sunsetEndDateRef.current)
          : now,
      })

      const endsIn = formatDuration(sunsetEndDuration, {
        format: ['months', 'days', 'hours', 'minutes', 'seconds'],
        delimiter: '',
        zero: true,
      })
        .replace(/0 months/, '')
        .replace(/ months?/, 'm')
        .replace(/ days?/, 'd')
        .replace(/ hours?/, 'h')
        .replace(/ minutes?/, 'm')
        .replace(/ seconds?/, 's')
      setSunsetEndsIn(endsIn)
      setSunsetEndsInDays(
        formatDuration(sunsetEndDuration, {
          format: ['months', 'days'],
          delimiter: ' and ',
        })
      )
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <Spacing
        $center
        $verticalSize="hero"
        $spaceChildrenSize="xxlarge"
        $borderedBottom
        css={`
          @media (max-width: 539px) {
            padding-top: 30px;
            padding-bottom: 30px;
          }
        `}
      >
        <LargeExpandedCopyWrapper>
          <Image
            src="./assets/images/dharma_opensea.png"
            alt="Dharma + OpenSea = ❤️"
          />
        </LargeExpandedCopyWrapper>
        <ExpandedCopyWrapper css="max-width: 820px;">
          <FadeInWhenVisible>
            <Spacing
              $spaceChildrenSize="large"
              $alignItems="center"
              $alignItemsOnMobile="flex-start"
              $textAlign="center"
              $textAlignOnMobile="left"
            >
              <CopyTitle
                level={3}
                fontSize="36px"
                lineHeight="46px"
                fontSizeOnMobile="24px"
                lineHeightOnMobile="32px"
              >
                Dharma is excited to be joining the OpenSea team.
              </CopyTitle>
              <CopyTitle
                level={3}
                secondary
                fontSize="36px"
                lineHeight="46px"
                fontSizeOnMobile="24px"
                lineHeightOnMobile="32px"
              >
                At OpenSea, we will continue our mission to make crypto
                accessible to the mainstream. We thank you for being users,
                fans, and community members.
              </CopyTitle>
              <Link
                href="https://dharma.mirror.xyz/AiAKDL49ChgZmx-D-o_YmP-9zVDl2nqsD4uOwTcXBnI"
                target="_blank"
              >
                <Button buttonType="primary">Read the blog post</Button>
              </Link>
            </Spacing>
          </FadeInWhenVisible>
        </ExpandedCopyWrapper>
      </Spacing>
      <Spacing
        $center
        $verticalSize="hero"
        $spaceChildrenSize="xxlarge"
        $borderedBottom
        css={`
          @media (max-width: 539px) {
            padding-top: 30px;
            padding-bottom: 30px;
          }
        `}
      >
        <LargeExpandedCopyWrapper>
          <FadeInWhenVisible>
            <Spacing
              $flexDirection="row"
              $bottomSize="xxlarge"
              $justifyContent="space-between"
              $alignItems="center"
              $spaceChildrenSize="large"
              $flexDirectionOnMobile="column"
              $textAlignOnMobile="left"
            >
              <Spacing css="max-width: 600px;" $spaceChildrenSize="large">
                <div>
                  <CopyTitle
                    level={3}
                    accented
                    fontSize="36px"
                    lineHeight="46px"
                    fontSizeOnMobile="24px"
                    lineHeightOnMobile="32px"
                  >
                    Are you a Dharma user?
                  </CopyTitle>
                  <CopyTitle
                    level={3}
                    fontSize="36px"
                    lineHeight="46px"
                    fontSizeOnMobile="24px"
                    lineHeightOnMobile="32px"
                  >
                    Here’s what you need to know:
                  </CopyTitle>
                </div>
                <CopyTitle level={4} lineHeight="34px">
                  Dharma is shutting down in {sunsetEndsInDays}. Don’t worry -
                  you won’t lose your assets.{' '}
                  <Span error>
                    Please transfer or sell all tokens in your wallet within{' '}
                    {sunsetEndsInDays}.
                  </Span>
                </CopyTitle>
              </Spacing>
              <Card type="flat-no-button-shadow" css="max-width: 350px;">
                <Spacing $size="medium" $spaceChildrenSize="medium">
                  <EmojiWithTextEntry
                    bold
                    emoji="🔁"
                    text={
                      <Span error>
                        You can’t swap or purchase tokens on Dharma anymore.
                      </Span>
                    }
                  />
                  <EmojiWithTextEntry
                    bold
                    emoji="🏦"
                    text={
                      <>
                        You can sell any asset to your bank,{' '}
                        <Span accented>we pay the network fees.*</Span>
                      </>
                    }
                  />
                  <EmojiWithTextEntry
                    bold
                    emoji="✈️"
                    text={
                      <>
                        You can transfer any asset to a different wallet,{' '}
                        <Span accented>we pay the network fees.*</Span>
                      </>
                    }
                  />
                  <EmojiWithTextEntry
                    emoji=""
                    text={
                      <>
                        <Span secondary>
                          *Applicable to any transaction over{' '}
                          {networkFeeWaivedThreshold
                            ? formatToUSD(
                                new BigNumber(networkFeeWaivedThreshold)
                              )
                            : '...'}
                        </Span>
                      </>
                    }
                  />
                </Spacing>
              </Card>
            </Spacing>
          </FadeInWhenVisible>
          <FadeInWhenVisible>
            <Spacing
              $center
              $topSize="xxlarge"
              $spaceChildrenSize="large"
              css={`
                @media (max-width: 539px) {
                  padding-top: 0px;
                }
              `}
            >
              <CopyTitle level={4} secondary>
                Countdown clock
              </CopyTitle>
              <CopyTitle
                fontSize="120px"
                lineHeight="110px"
                fontSizeOnMobile="48px"
                lineHeightOnMobile="44px"
              >
                {sunsetEndsIn}
              </CopyTitle>
            </Spacing>
          </FadeInWhenVisible>
        </LargeExpandedCopyWrapper>
      </Spacing>
      <FadeInWhenVisible>
        <Spacing
          $center
          $verticalSize="hero"
          $spaceChildrenSize="xxlarge"
          css={`
            @media (max-width: 539px) {
              padding-top: 30px;
              padding-bottom: 30px;
            }
          `}
        >
          <LargeExpandedCopyWrapper>
            <Spacing $spaceChildrenSize="xlarge">
              <Spacing $textAlign="center" $textAlignOnMobile="left">
                <CopyTitle>Frequently Asked Questions (FAQ)</CopyTitle>
              </Spacing>
              <Spacing>
                {getFAQs(endDate, networkFeeWaivedThreshold).map(
                  ([question, text], index) => (
                    <FAQDropdown question={question} text={text} key={index} />
                  )
                )}
              </Spacing>
            </Spacing>
          </LargeExpandedCopyWrapper>
        </Spacing>
      </FadeInWhenVisible>
    </>
  )
}

Home.getLayout = function getLayout(page: React.ReactElement) {
  return <NavAndFooterLayout>{page}</NavAndFooterLayout>
}

export default Home
